﻿<script setup lang="ts">

    import EditSession from '@/shared/codeedit/EditSession.vue';
    import CodeRefSelector from '@/shared/components/CodeRefSelector.vue';
    import PipeClassEditorField from '@/apps/STID_Pipe/PipeClassEditorField.vue';
    import TypedBoolSelector from '@/shared/components/TypedBoolSelector.vue';
    import Spinner from '@/shared/components/Spinner.vue';
    import { usePipeClassStore } from '@/stores/pipeClassStore.ts';
    import { computed, onMounted, ref } from 'vue';
    import { CodeEditSessionState } from '@/shared/codeedit/codeEditSessionState.ts';
    import { CodeEditFields } from '@/shared/codeedit/codeEditFields.ts';
    import tminCalculations, { PipingClassComputedValues } from '@/apps/STID_Pipe/pipingClassSizes.ts';
    import { CodeEditContextRequest } from '@/shared/helpers/api.ts';
    import PipeClassEditorComputedField from '@/apps/STID_Pipe/PipeClassEditorComputedField.vue';

    const editState = new CodeEditSessionState();
    const pipeClassStore = usePipeClassStore();
    const isLoading = ref<boolean>(false);

    function calculations(code: CodeEditFields): PipingClassComputedValues | undefined {
        if (pipeClassStore.selectedPipingClass !== null && pipeClassStore.selectedScope !== null) {
            return tminCalculations(pipeClassStore.selectedScope, code, pipeClassStore.selectedPipingClass);
        }
    }

    const lineSizesFiltered = computed(() => {
        return editState.referableCodes.value['LineSize']?.filter((ls) => ls.isValid) ?? [];
    });

    export type CodeProp = {
        id: number;
        isNew: false;
    } | {
        id: null;
        isNew: true;
    };
    const props = defineProps<{
        code: CodeProp;
    }>();

    const emit = defineEmits<{
        refresh: [];
        cancel: [];
    }>();

    const computedPipeValues = computed(() => {
        if (editState.code.value) {
            return calculations(editState.code.value.modified);
        }
        return null;
    });

    const lineSizeData = computed(() => {
        const currentLineSizeIdentity = editState.code.value?.modified.LineSize;
        return pipeClassStore.lineSizes.find(ls => ls.Identity === currentLineSizeIdentity);
    });

    async function init() {
        if (!pipeClassStore.currentPipingClassSizeCodeSetName) {
            throw new Error(`CodeSetName is not set`);
        }

        isLoading.value = true;

        const r: CodeEditContextRequest = { codeSetName: pipeClassStore.currentPipingClassSizeCodeSetName };

        let id;
        if (props.code.isNew) {
            id = editState.base.nextNewTmpId();
        } else {
            id = props.code.id;
            r.codeId = id;
        }

        await editState.loadFromContext(id, r, props.code.isNew);

        const currentPipingClass = pipeClassStore.selectedPipingClass?.Identity;
        if (currentPipingClass === undefined) {
            throw new Error('No piping class is selected');
        }
        editState.setCodeTemplateValues({
            'PipingClass': currentPipingClass
        });

        editState.applyTemplateValues();
        isLoading.value = false;
    }

    function handleLineSizeChanged(value: string, attr: string) {
        editState.setFieldValue(attr, value);
        editState.setFieldValue('name', value);
    }

    onMounted(async () => {
        await init();
    });

    function onCancelClicked() {
        emit('cancel');
    }

    function onSavedPipingClassSize() {
        emit('refresh');
    }
</script>

<template>
    <spinner :loading="isLoading">
        <edit-session
            v-if="editState.code.value"
            :edit-session-state="editState.base"
            :can-edit-code-options="{ libraryName: 'PipingClassSize', scopes: pipeClassStore.selectedScope ? [pipeClassStore.selectedScope] : [] }"
            :use-release-chain="false"
            submit-button-label="Submit"
            @cancel="onCancelClicked"
            @refresh="onSavedPipingClassSize">
            <b-notification
                v-if="editState.code.value.identityConflict"
                type="is-danger is-light"
                :closable="false"
                has-icon
                role="alert">
                <p><b>An item with this line size already exists!</b></p>
                <p>
                    {{ editState.code.value.identityConflict.message }}
                </p>
            </b-notification>
            <div class="fixed-grid has-2-cols">
                <div class="grid">
                    <div class="cell is-row-span-3">
                        <div class="fixed-grid has-1-cols">
                            <div class="grid">
                                <pipe-class-editor-field
                                    field="PipingClass"
                                    :clearable="false"
                                    :state="editState.code.value"
                                    label="Piping class">
                                    <code-ref-selector
                                        :selected="pipeClassStore.selectedPipingClass?.Identity"
                                        disabled
                                        :code-refs="editState.referableCodes.value.PipingClass ?? []" />
                                </pipe-class-editor-field>
                                <pipe-class-editor-field
                                    field="isValid"
                                    :clearable="false"
                                    label="Is valid"
                                    :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                                    :state="editState.code.value">
                                    <typed-bool-selector v-model="editState.code.value.modified.isValid" />
                                </pipe-class-editor-field>
                            </div>
                        </div>
                    </div>
                    <div class="cell is-row-span-3 fixed-grid has-1-cols">
                        <div class="fixed-grid has-1-cols">
                            <div class="grid">
                                <pipe-class-editor-field
                                    field="LineSize"
                                    class="cell"
                                    :clearable="false"
                                    :state="editState.code.value"
                                    label="Line size">
                                    <code-ref-selector
                                        required
                                        :selected="editState.code.value.modified.LineSize"
                                        :disabled="!editState.code.value.metadata.isNew"
                                        :code-refs="lineSizesFiltered"
                                        @ref-selected="(v: string) => handleLineSizeChanged(v, 'LineSize')" />
                                </pipe-class-editor-field>
                                <pipe-class-editor-computed-field
                                    label="Nom Diameter Unit"
                                    class="cell"
                                    :value="lineSizeData?.NomDiamUnit" />
                                <pipe-class-editor-computed-field
                                    label="Nom Diameter [inches]"
                                    :value="lineSizeData?.NomDiamInches"
                                    class="cell" />
                            </div>
                        </div>
                    </div>
                    <pipe-class-editor-field
                        class="cell"
                        label="Wall Thickness [mm]"
                        field="WallThickness"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input
                            v-model="editState.code.value.modified.WallThickness"
                            step="0.01"
                            type="number" />
                    </pipe-class-editor-field>
                    <div class="cell is-col-span-2">
                        <div class="fixed-grid has-2-cols">
                            <div class="grid">
                                <pipe-class-editor-field
                                    class="cell"
                                    field="OuterDiameter"
                                    label="OuterDiameter [mm]"
                                    :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                                    :state="editState.code.value">
                                    <b-input
                                        v-model.number="editState.code.value.modified.OuterDiameter"
                                        step="0.01"
                                        type="number" />
                                </pipe-class-editor-field>
                                <pipe-class-editor-computed-field
                                    class="cell"
                                    :value="computedPipeValues?.inDiam"
                                    label="Inner diameter [mm]" />
                            </div>
                        </div>
                    </div>
                    <pipe-class-editor-field
                        class="cell"
                        field="Stress"
                        label="Stress [N/mm²]"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input
                            v-model="editState.code.value.modified.Stress"
                            step="0.01"
                            type="number" />
                    </pipe-class-editor-field>
                    <pipe-class-editor-field
                        class="cell"
                        field="Schedule"
                        label="Schedule"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input v-model="editState.code.value.modified.Schedule" />
                    </pipe-class-editor-field>
                    <pipe-class-editor-field
                        class="cell"
                        field="QualityFactor"
                        label="Quality Factor"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input
                            v-model="editState.code.value.modified.QualityFactor"
                            step="0.01"
                            type="number" />
                    </pipe-class-editor-field>
                    <pipe-class-editor-field
                        class="cell"
                        field="YFactor"
                        label="YFactor"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input
                            v-model="editState.code.value.modified.YFactor"
                            step="0.01"
                            type="number" />
                    </pipe-class-editor-field>
                    <div class="cell is-row-span-3">
                        <div class="fixed-grid has-1-cols">
                            <div class="grid">
                                <pipe-class-editor-computed-field
                                    class="cell"
                                    :value="computedPipeValues?.tmin1"
                                    label="Tmin 1[mm]" />
                                <pipe-class-editor-computed-field
                                    class="cell"
                                    :value="computedPipeValues?.tmin2"
                                    label="Tmin 2[mm]" />
                                <pipe-class-editor-computed-field
                                    class="cell"
                                    :value="computedPipeValues?.tmin3"
                                    label="Tmin 3[mm]" />
                            </div>
                        </div>
                    </div>
                    <pipe-class-editor-field
                        class="cell"
                        field="DesignPressureMaximum"
                        label="Max design pressure"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input
                            v-model="editState.code.value.modified.DesignPressureMaximum"
                            class="is-flex-grow-1"
                            step="0.01"
                            type="number" />
                        <template #unit>
                            <b-input
                                :model-value="computedPipeValues?.sUnit"
                                disabled />
                        </template>
                    </pipe-class-editor-field>
                    <pipe-class-editor-field
                        class="cell"
                        field="UpdateRef"
                        label="Reference"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input v-model="editState.code.value.modified.UpdateRef" />
                    </pipe-class-editor-field>
                    <pipe-class-editor-field
                        field="Comments"
                        label="Comments"
                        :set-field-value="(f,v) => editState.setFieldValue(f,v)"
                        :state="editState.code.value">
                        <b-input v-model="editState.code.value.modified.Comments" />
                    </pipe-class-editor-field>
                </div>
            </div>
        </edit-session>
    </spinner>
</template>

<style scoped>

</style>
